// client/src/components/pages/LexiStar/Approvals/Approvals.tsx
//
// LexiStar deviation approvals queue (ST-00188, M2 T6-S07; LS-09 §5.4,
// FR-DEV-005). Route /lexistar/approvals; page permission lexistar.trips.view,
// decisions lexistar.deviations.approve.
//
// Split view: the queue on the left (severity, age, SLA countdown, driver-
// replied indicator; most severe then oldest first) and, for the selected
// deviation, the map (planned corridor, actual track, current position), the
// trip context, the driver's reply, and the four actions — each with a reason
// code (m2-contracts §3.1 kind 'deviation'):
//   Approve once                   POST /deviations/:id:approve  { reason_code, extra_km? }
//   Approve + temporary corridor   … { reason_code, create_corridor: { scope, expiry } }
//   Reject — return to route       POST /deviations/:id:reject   { reason_code }
//   Escalate                       POST /deviations/:id:escalate { reason_code }
// Keyboard: A / R / E open the action, J / K move through the queue, Esc
// cancels. The selection lives in the URL (?d=); a link that names a deviation
// (?deviation=<id>: in-app alerts, the link page's controller redirect, KPI
// drill-downs) opens it, and that alias is dropped as soon as the selection
// moves (ST-00202). Live via the tenant's approvals room.
//
// ST-00188 (WhatsApp runbook §5.8): the driver's reply shows the photos the
// driver sent (`driver_reason_media`, DriverPhotos: thumbnails, a lightbox,
// "Photo unavailable" when one cannot be shown), and reads the reply as the
// engine stores it (`reason_code`, `at`) as well as the UI's first reading
// (`code`, `replied_at`) — see driverReply.ts. Converted to TypeScript in the
// same change (CLAUDE.md rule); behaviour otherwise unchanged.
//
// ST-00207 (m4b-contracts §5, phone pass): under 640 px or on a touch screen the page is the
// phone layout of ApprovalsPhone.tsx — the queue as cards, the queue choice in a bottom sheet,
// one deviation per screen with Approve / Reject / Escalate in a sticky bottom bar, the map as
// a panel with a full-screen toggle. A chat link (?deviation=<id>) lands on that deviation with
// the bar showing; a card opens one with a history entry, so the phone's Back returns to the
// queue. On a phone a deviation that leaves the queue under the controller (or a link to one
// that is not waiting) goes back to the queue with a notice instead of opening another one.
// The desktop split view is unchanged; the shared pieces moved to approvalParts.tsx.

import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  FaRoute,
  FaSyncAlt,
  FaKeyboard,
  FaCheck,
  FaTimes,
  FaLevelUpAlt,
  FaDrawPolygon,
  FaReply,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { SegmentedControl, useAuth } from '../../../../types/commonComponents';
import {
  listDeviations,
  approveDeviation,
  rejectDeviation,
  escalateDeviation,
  pickList,
} from '../../../../services/lexistarStatusBoard';
import { driverPhotosOf } from '../../../../services/lexistarMedia';
import useLexiStarEvents from '../StatusBoard/useLexiStarEvents';
import {
  ErrorBanner,
  NoticeBanner,
  LoadingState,
  NoPermissionPage,
  ConnectionIndicator,
  useReasonCodes,
  cardClasses,
} from '../StatusBoard/atoms';
import { fmtNum, fmtKm, fmtDuration, fmtTime, secondsSince } from '../StatusBoard/format';
import { TONES } from '../StatusBoard/statusLanguage';
import useRouteGeometries from './useRouteGeometries';
import { QUEUE_FILTERS, ACTIONS, sortQueue, slaCountdown, shortcutFor } from './approvalLogic';
import {
  CorridorMap,
  DecisionPanel,
  DriverReplyContent,
  EmptyState,
  LIVE_REFETCH_DEBOUNCE_MS,
  PageHeader,
  SeverityChip,
  TripContextList,
  DECIDABLE_STATES,
  decisionStatusNote,
  errMessage,
  registrationOf,
  type ActionId,
  type DecideFn,
  type DeviationRow,
  type ReasonCode,
} from './approvalParts';
import { PhoneApprovalsQueue, PhoneDecisionBar, PhoneDeviationDetail } from './ApprovalsPhone';
import PhoneFilterSheet from '../../../common/PhoneFilterSheet';
import { scrollPaneToTop, usePhoneLayout } from '../../../common/phoneLayout';

/** History state of a deviation a phone opened from the queue (Back returns to the queue). */
const FROM_QUEUE = { lexistarApprovalsFromQueue: true };

function QueueItem({
  row,
  selected,
  onSelect,
  now,
}: {
  row: DeviationRow;
  selected: boolean;
  onSelect: (id: string) => void;
  now: number;
}) {
  const sla = slaCountdown(row, now);
  const age = secondsSince(row.opened_at, now);
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(row.id)}
        aria-current={selected ? 'true' : undefined}
        aria-label={`${row.trip?.registration || row.asset_key} deviation`}
        className={`w-full text-left px-4 py-3 border-l-4 transition-colors ${selected ? 'bg-primary-50 border-l-primary-500' : 'border-l-transparent hover:bg-slate-50'}`}
      >
        <div className="flex items-center justify-between gap-2">
          <SeverityChip severity={row.severity} />
          <span
            className={`text-xs tabular-nums font-semibold ${sla.overdue ? 'text-red-700' : 'text-slate-600'}`}
            title="Time left to decide"
          >
            SLA {sla.text}
          </span>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-sm font-mono font-semibold text-slate-900">
            {row.trip?.registration || row.asset_key}
          </span>
          <span className="text-xs text-slate-500 tabular-nums">opened {fmtDuration(age)} ago</span>
        </div>
        <div className="mt-0.5 text-xs text-slate-600 truncate">
          {row.trip?.from_name} → {row.trip?.to_name} · {fmtKm(row.off_route_km, 1)} off route
        </div>
        {!!row.driver_reason && (
          <span className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
            <FaReply size={9} /> Driver replied
          </span>
        )}
      </button>
    </li>
  );
}

function DeviationDetail({
  row,
  canDecide,
  codes,
  pending,
  setPending,
  onDecide,
  busy,
  decisionError,
  now,
}: {
  row: DeviationRow;
  canDecide: boolean;
  codes: ReasonCode[];
  pending: ActionId | null;
  setPending: (p: ActionId | null) => void;
  onDecide: DecideFn;
  busy: boolean;
  decisionError: string | null;
  now: number;
}) {
  const { lines, corridors, failed } = useRouteGeometries(
    // The hook skips a ref without a version (useRouteGeometries.js `wanted`).
    (row.trip?.route
      ? [{ route_id: row.trip.route.id, route_version: row.trip.route.version }]
      : []) as Array<{ route_id: string; route_version: number }>
  );
  const photos = useMemo(() => driverPhotosOf(row), [row]);
  const status = decisionStatusNote({ row, canDecide, codes });

  return (
    <div className="space-y-4">
      <div className={`${cardClasses} p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              <span className="font-mono">{row.trip?.registration || row.asset_key}</span>
              <span className="text-slate-500 font-normal">
                {' '}
                · {row.trip?.driver_name || row.trip?.driver_tag || 'No driver'}
              </span>
            </h2>
            <p className="text-sm text-slate-600 mt-0.5">
              <Link to={`/lexistar/trips/${row.trip_id}`} className="font-mono hover:underline">
                {row.trip?.reference}
              </Link>{' '}
              · {row.trip?.route?.name || `${row.trip?.from_name} → ${row.trip?.to_name}`}
              {row.trip?.route?.version != null && ` (v${row.trip.route.version})`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SeverityChip severity={row.severity} />
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium">
              {row.state.replace(/_/g, ' ').toLowerCase()}
            </span>
            {row.confidence != null && (
              <span className="text-xs text-slate-500" title="Confidence the deviation is real">
                {row.confidence}% confidence
              </span>
            )}
          </div>
        </div>

        <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500 uppercase tracking-wider">Max off route</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {fmtNum(row.max_offset_m)} m
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 uppercase tracking-wider">Off-route distance</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {fmtKm(row.off_route_km, 1)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 uppercase tracking-wider">Elapsed</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {fmtDuration(secondsSince(row.opened_at, now))}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 uppercase tracking-wider">
              Stops while off route
            </dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {(row.stops || []).length}
            </dd>
          </div>
        </dl>
      </div>

      <CorridorMap
        idPrefix="approvals"
        routes={lines}
        corridors={corridors}
        track={row.track}
        deviations={
          row.track ? [{ id: row.id, geometry: row.track, approved: row.state === 'APPROVED' }] : []
        }
        position={row.position}
        positionColor={TONES.loaded.hex}
        stops={row.stops || []}
      />
      {(failed || !row.track) && (
        <p className="text-xs text-slate-500">
          {failed && 'The planned route could not be loaded. '}
          {!row.track && 'No off-route track has been received for this deviation yet.'}
        </p>
      )}
      {row.position && (
        <p className="text-xs text-slate-500">
          Last position {fmtTime(row.position.ts)}
          {row.position.speed_kmh != null && ` · ${Math.round(row.position.speed_kmh)} km/h`}
        </p>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <section className={`${cardClasses} p-4`} aria-label="Trip context">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Trip</h3>
          <TripContextList row={row} />
        </section>

        <section className={`${cardClasses} p-4`} aria-label="Driver reply">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Driver's reply</h3>
          <DriverReplyContent row={row} codes={codes} photos={photos} />
        </section>
      </div>

      <section className={`${cardClasses} p-4`} aria-label="Decision">
        {status ?? (
          <>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['approve', FaCheck],
                  ['approve_corridor', FaDrawPolygon],
                  ['reject', FaTimes],
                  ['escalate', FaLevelUpAlt],
                ] as Array<[ActionId, IconType]>
              ).map(([id, Icon]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPending(id)}
                  aria-pressed={pending === id}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 min-h-[48px] text-sm font-semibold rounded-lg transition-all ${ACTIONS[id].className}`}
                >
                  <Icon size={12} />
                  {ACTIONS[id].label}
                  {ACTIONS[id].key && (
                    <kbd className="ml-1 px-1.5 py-0.5 rounded bg-black/10 text-[10px] font-mono">
                      {ACTIONS[id].key}
                    </kbd>
                  )}
                </button>
              ))}
            </div>
            {pending && (
              <DecisionPanel
                key={`${row.id}:${pending}`}
                action={pending}
                row={row}
                codes={codes}
                busy={busy}
                error={decisionError}
                onCancel={() => setPending(null)}
                onConfirm={onDecide}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}

const Approvals = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.trips.view');
  const canDecide = hasPermission('lexistar.deviations.approve');
  const codes = useReasonCodes('deviation') as ReasonCode[];
  const phone = usePhoneLayout();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const filter = QUEUE_FILTERS.some((f) => f.value === searchParams.get('queue'))
    ? (searchParams.get('queue') as string)
    : 'pending';
  const selectedId = searchParams.get('deviation') ?? searchParams.get('d');

  const [rows, setRows] = useState<DeviationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<ActionId | null>(null);
  const [busy, setBusy] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const setParams = useCallback(
    (changes: Record<string, string>, { push = false }: { push?: boolean } = {}) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      // ?deviation= only opens the page on a deviation (in-app alerts, the link page's
      // controller redirect, KPI drill-downs); once the selection moves, ?d= alone holds it,
      // or the alias would pin the page to the linked deviation (ST-00202).
      if ('d' in changes) next.delete('deviation');
      // ST-00207: a phone opening a deviation from the queue pushes a history entry (Back
      // returns to the queue); every other change replaces the entry and keeps its state.
      if (push) setSearchParams(next, { state: FROM_QUEUE });
      else setSearchParams(next, { replace: true, state: location.state });
    },
    [searchParams, setSearchParams, location.state]
  );

  /**
   * Phone: back from one deviation to the queue — through history when the queue opened it
   * (so the phone's Back button does not land on the queue twice), else by dropping ?d=.
   */
  const leaveDetail = useCallback(() => {
    const state = location.state as typeof FROM_QUEUE | null;
    if (state?.lexistarApprovalsFromQueue) navigate(-1);
    else setParams({ d: '' });
  }, [location.state, navigate, setParams]);

  const states = QUEUE_FILTERS.find((f) => f.value === filter)!.states;

  const fetchQueue = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!canView) return;
      try {
        if (!quiet) setLoading(true);
        const res = await listDeviations({ state: states });
        setRows(sortQueue(pickList(res, 'deviations')) as DeviationRow[]);
        setError(null);
      } catch (err) {
        const e = err as { response?: { data?: { error?: string } } } | null;
        if (!quiet) setError(e?.response?.data?.error || 'Failed to load the approvals queue.');
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [canView, states]
  );

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (!canView) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(refetchTimer.current);
    };
  }, [canView]);

  const connection = useLexiStarEvents({
    approvals: true,
    enabled: canView,
    onEvent: (evt: { type?: unknown }) => {
      if (!String(evt.type || '').startsWith('ROUTE.')) return;
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(
        () => fetchQueue({ quiet: true }),
        LIVE_REFETCH_DEBOUNCE_MS
      );
    },
  });

  // Desktop: the first deviation is always open. Phone: the queue until one is opened.
  const selected = useMemo(() => {
    const hit = rows.find((r) => r.id === selectedId) || null;
    return phone ? hit : hit || rows[0] || null;
  }, [rows, selectedId, phone]);

  // Keep the URL on a row that exists, and say so when the deviation on screen
  // left the queue under us (decided elsewhere, closed, voided).
  const previousIds = useRef<Set<string>>(new Set());
  const lastShownId = useRef<string | null>(null);
  const decidedHere = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (loading) return;
    const ids = new Set(rows.map((r) => r.id));
    const shown = lastShownId.current;
    if (
      shown &&
      !ids.has(shown) &&
      previousIds.current.has(shown) &&
      !decidedHere.current.has(shown)
    ) {
      setNotice('That deviation is no longer in this queue — it was decided or closed.');
    }
    if (selectedId && !ids.has(selectedId)) {
      if (phone) {
        // A phone shows one deviation at a time: never slip another one under the
        // controller's thumb — back to the queue, and say why when a link named it.
        if (!previousIds.current.has(selectedId) && !decidedHere.current.has(selectedId)) {
          setNotice(
            'The deviation in the link is not waiting in this queue — it was decided, escalated or closed.'
          );
        }
        leaveDetail();
      } else setParams({ d: rows[0]?.id || '' });
    }
    previousIds.current = ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, loading]);
  useEffect(() => {
    lastShownId.current = selected?.id || null;
  }, [selected]);

  const select = useCallback(
    (id: string) => {
      setPending(null);
      setDecisionError(null);
      setParams({ d: id });
    },
    [setParams]
  );

  const move = useCallback(
    (delta: number) => {
      if (!rows.length) return;
      const idx = rows.findIndex((r) => r.id === selected?.id);
      const next = idx < 0 ? rows[0] : rows[Math.min(rows.length - 1, Math.max(0, idx + delta))];
      if (next) select(next.id);
    },
    [rows, selected, select]
  );

  // A layout effect, so the listener that sees the new selection is in place as soon as the page
  // shows it: a key pressed right after the selection changes never reaches the previous listener.
  useLayoutEffect(() => {
    if (!canView) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPending(null);
        return;
      }
      const action = shortcutFor(e) as ActionId | 'next' | 'previous' | null;
      if (!action) return;
      if (action === 'next') move(1);
      else if (action === 'previous') move(-1);
      else if (
        canDecide &&
        selected &&
        selected.can_decide !== false &&
        DECIDABLE_STATES.includes(selected.state)
      ) {
        setDecisionError(null);
        setPending(action);
      } else return;
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canView, canDecide, selected, move]);

  const decide: DecideFn = async (action, payload, reasonLabel) => {
    if (!selected) return;
    setBusy(true);
    setDecisionError(null);
    try {
      if (action === 'approve' || action === 'approve_corridor')
        await approveDeviation(selected.id, payload);
      else if (action === 'reject') await rejectDeviation(selected.id, payload);
      else await escalateDeviation(selected.id, payload);
      decidedHere.current.add(selected.id);
      const idx = rows.findIndex((r) => r.id === selected.id);
      const remaining = rows.filter((r) => r.id !== selected.id);
      const next = remaining[Math.min(idx, remaining.length - 1)];
      setRows(remaining);
      setPending(null);
      setNotice(
        `${ACTIONS[action].verb} ${selected.trip?.reference || 'the deviation'} (${reasonLabel}).`
      );
      if (phone && !next) leaveDetail();
      else setParams({ d: next?.id || '' });
      if (phone) scrollPaneToTop(rootRef.current);
    } catch (err) {
      setDecisionError(errMessage(err, 'The decision was not saved'));
    } finally {
      setBusy(false);
    }
  };

  // ── Phone: open / leave a deviation ────────────────────────────────────────
  const openFromQueue = (id: string) => {
    setPending(null);
    setDecisionError(null);
    setParams({ d: id }, { push: true });
    scrollPaneToTop(rootRef.current);
  };
  const backToQueue = () => {
    setPending(null);
    setDecisionError(null);
    leaveDetail();
    scrollPaneToTop(rootRef.current);
  };
  const phoneMove = (delta: number) => {
    move(delta);
    scrollPaneToTop(rootRef.current);
  };
  const chooseOnPhone = (action: ActionId) => {
    setDecisionError(null);
    setPending((p) =>
      action === 'approve' && (p === 'approve' || p === 'approve_corridor') ? p : action
    );
  };

  const queueFilter = QUEUE_FILTERS.find((f) => f.value === filter)!;
  const header = (
    <PageHeader
      icon={FaRoute}
      title="Deviation Approvals"
      subtitle="Trucks off their planned route, waiting for a decision."
      toolbar={
        canView ? (
          phone ? (
            selected ? null : (
              <PhoneFilterSheet
                title="Choose the queue"
                label="Queue"
                value={`${queueFilter.label}${loading ? '' : ` · ${rows.length}`}`}
                groups={[
                  {
                    id: 'queue',
                    legend: 'Queue',
                    value: filter,
                    choices: QUEUE_FILTERS.map((f) => ({
                      value: f.value,
                      label: f.label,
                      count: f.value === filter && !loading ? rows.length : null,
                    })),
                  },
                ]}
                onApply={(v) =>
                  setParams({ queue: v.queue === 'pending' ? '' : v.queue || '', d: '' })
                }
              />
            )
          ) : (
            <SegmentedControl<string>
              options={QUEUE_FILTERS.map((f) => ({
                value: f.value,
                label: f.label,
                badge: f.value === filter ? rows.length : undefined,
              }))}
              value={filter}
              onChange={(v) => setParams({ queue: v === 'pending' ? '' : v, d: '' })}
              ariaLabel="Queue"
            />
          )
        ) : null
      }
      actions={
        canView ? (
          <div className="flex items-center gap-3">
            <ConnectionIndicator status={connection} compact={phone} />
            {!phone && (
              <button
                type="button"
                onClick={() => setShowKeys((v) => !v)}
                aria-expanded={showKeys}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <FaKeyboard size={12} /> <span className="hidden sm:inline">Shortcuts</span>
              </button>
            )}
            <button
              onClick={() => fetchQueue()}
              className={`${phone ? 'touch-target justify-center ' : ''}inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50`}
              aria-label="Refresh"
            >
              <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view deviation approvals."
      />
    );
  }

  if (phone) {
    const index = selected ? rows.findIndex((r) => r.id === selected.id) : -1;
    const showBar =
      !!selected &&
      canDecide &&
      selected.can_decide !== false &&
      decisionStatusNote({ row: selected, canDecide, codes }) === null;
    return (
      <div ref={rootRef} className="min-h-screen flex flex-col bg-slate-50" data-phone-layout="">
        {header}
        <div className={`flex-1 pt-4 space-y-3 ${showBar ? 'pb-4' : 'pb-20'}`}>
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
          {loading && rows.length === 0 ? (
            <LoadingState label="Loading the approvals queue..." />
          ) : selected ? (
            <PhoneDeviationDetail
              key={selected.id}
              row={selected}
              index={index}
              total={rows.length}
              onBack={backToQueue}
              onMove={phoneMove}
              canDecide={canDecide}
              codes={codes}
              pending={pending}
              onApproveMode={(a) => setPending(a)}
              onCancel={() => setPending(null)}
              onDecide={decide}
              busy={busy}
              decisionError={decisionError}
              now={now}
            />
          ) : rows.length === 0 ? (
            <div className={cardClasses}>
              <EmptyState
                icon={FaRoute}
                title={filter === 'pending' ? 'Nothing waiting' : 'Nothing here'}
              >
                {filter === 'pending'
                  ? 'No truck is off its route awaiting a decision. New deviations appear here as they open.'
                  : 'No deviations in this list.'}
              </EmptyState>
            </div>
          ) : (
            <PhoneApprovalsQueue rows={rows} now={now} onOpen={openFromQueue} />
          )}
        </div>
        {showBar && selected && (
          <PhoneDecisionBar
            registration={registrationOf(selected)}
            pending={pending}
            onChoose={chooseOnPhone}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
        {showKeys && (
          <div
            role="note"
            className="mb-4 px-4 py-3 bg-surface border border-slate-200 rounded-lg text-sm text-slate-700 flex flex-wrap gap-x-6 gap-y-1"
          >
            <span>
              <kbd className="font-mono font-bold">A</kbd> approve
            </span>
            <span>
              <kbd className="font-mono font-bold">R</kbd> reject
            </span>
            <span>
              <kbd className="font-mono font-bold">E</kbd> escalate
            </span>
            <span>
              <kbd className="font-mono font-bold">J</kbd> /{' '}
              <kbd className="font-mono font-bold">K</kbd> next / previous
            </span>
            <span>
              <kbd className="font-mono font-bold">Esc</kbd> cancel
            </span>
          </div>
        )}

        {loading && rows.length === 0 ? (
          <LoadingState label="Loading the approvals queue..." />
        ) : rows.length === 0 ? (
          <div className={cardClasses}>
            <EmptyState
              icon={FaRoute}
              title={filter === 'pending' ? 'Nothing waiting' : 'Nothing here'}
            >
              {filter === 'pending'
                ? 'No truck is off its route awaiting a decision. New deviations appear here as they open.'
                : 'No deviations in this list.'}
            </EmptyState>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-6">
            <aside className={`${cardClasses} overflow-hidden self-start`}>
              <h2 className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                {rows.length} deviation{rows.length === 1 ? '' : 's'} · most severe first
              </h2>
              <ul className="divide-y divide-slate-100" aria-label="Approvals queue">
                {rows.map((r) => (
                  <QueueItem
                    key={r.id}
                    row={r}
                    selected={selected?.id === r.id}
                    onSelect={select}
                    now={now}
                  />
                ))}
              </ul>
            </aside>
            {selected && (
              <DeviationDetail
                key={selected.id}
                row={selected}
                canDecide={canDecide}
                codes={codes}
                pending={pending}
                setPending={(p) => {
                  setDecisionError(null);
                  setPending(p);
                }}
                onDecide={decide}
                busy={busy}
                decisionError={decisionError}
                now={now}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;
